import { isPlatformBrowser } from '@angular/common';
import type { OnChanges, OnInit } from '@angular/core';
import {
	ChangeDetectorRef,
	Component,
	computed,
	HostListener,
	inject,
	input,
	PLATFORM_ID,
	signal,
} from '@angular/core';

import { AngularSvgIconModule } from 'angular-svg-icon';

import { cn } from '@valk-nx/core/lib/core';
import { LazyImageLoadingDirective } from '@valk-nx/core/lib/directives/lazy-image/lazy-image.directive';
import { ImageQueryPipe } from '@valk-nx/core/lib/pipes/image-query/image-query.pipe';
import { debounceFn } from '@valk-nx/helpers/lib/debounce/debounce.helper';

import type { ImageInterface, ImageSourceInterface } from './image.interface';
import { SizeValues } from './image.interface';

@Component({
	selector: 'vp-image',
	templateUrl: './image.component.html',
	imports: [AngularSvgIconModule, LazyImageLoadingDirective, ImageQueryPipe],
	providers: [ImageQueryPipe],
})
export class ImageComponent implements OnChanges, OnInit {
	readonly cd = inject(ChangeDetectorRef);
	private readonly platformId = inject(PLATFORM_ID);

	image = input<ImageInterface>();
	sources = input<ImageSourceInterface[]>([]);
	className = input<string>('');
	priority = input<boolean>(false);

	img = computed(() => {
		const image = this.image();

		if (!image?.src || this.fallbackImage()) {
			return {
				src: '/assets/images/application-icons/icon-transparent-96x96.png',
				width: 96,
				height: 96,
			};
		}

		return image;
	});

	showFallbackImage = computed(
		() => this.fallbackImage() || !this.image()?.src,
	);

	imageWidth = signal(0);
	fallbackImage = signal(false);

	resolvedSources: (ImageSourceInterface & { media: string })[] = [];

	cn = cn;

	private readonly debouncedSetImageWidth = debounceFn(() =>
		this.setImageWidth(),
	);

	ngOnInit() {
		this.setImageWidth();
	}

	ngOnChanges(): void {
		this.resolvedSources = [];
		for (const source of this.sources()) {
			let media = '';
			if (source.min) {
				media = `(min-width: ${SizeValues[source.min]}px)`;
			}
			if (source.min && source.max) {
				media = media + ' and ';
			}
			if (source.max) {
				media = media + `(max-width: ${SizeValues[source.max]}px)`;
			}

			this.resolvedSources.push({
				...source,
				media,
			});
		}
	}

	@HostListener('window:resize')
	onResize() {
		this.debouncedSetImageWidth();
	}

	setImageWidth() {
		if (isPlatformBrowser(this.platformId)) {
			const windowWidth = window.innerWidth;

			const imageSource = this.sources().find(
				(source) =>
					(source.max !== null &&
						windowWidth <= SizeValues[source.max]) ||
					(source.max === null &&
						source.min !== null &&
						windowWidth >= SizeValues[source.min]),
			);

			this.imageWidth.set(imageSource?.width ? imageSource.width : 0);

			this.cd.detectChanges();
		}
	}

	setFallbackImage() {
		this.fallbackImage.set(true);
		this.cd.detectChanges();
	}
}
